/**
 * Admin "User MFA Status" panel — User Management page.
 *
 * Lists every user the admin governs (GET /admin/mfa/users) with a
 * Reset MFA action. There is deliberately NO disable / secret view /
 * QR view / recovery-code view — resets force fresh enrollment.
 * Reset requires: recent admin MFA (step-up on 403), the explicit
 * `users.mfa.reset` permission (server-enforced) and a mandatory reason.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { ShieldWarning, ArrowCounterClockwise } from '@phosphor-icons/react';
import MfaStepUpDialog from '../auth/MfaStepUpDialog';

const statusBadge = (status) => {
  if (status === 'Enrolled') return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Enrolled</Badge>;
  if (status === 'Reset Required') return <Badge className="bg-amber-50 text-amber-700 border-amber-200">Reset Required</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 border-slate-200">Not Enrolled</Badge>;
};

export const MfaStatusPanel = () => {
  const { user: me } = useAuth();
  const [rows, setRows] = useState(null); // null = hidden (403 / not loaded)
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [resetting, setResetting] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/mfa/users');
      setRows(r.data.users || []);
    } catch {
      setRows(null); // non-admins simply don't see the panel
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doReset = useCallback(async () => {
    if (!target) return;
    setResetting(true);
    try {
      await api.post(`/admin/mfa/users/${target.id}/reset`, { reason: reason.trim() });
      toast.success(`MFA reset for ${target.email}. All their sessions are signed out; they must re-enroll at next login.`);
      setTarget(null);
      setReason('');
      load();
    } catch (err) {
      const structured = err.response?.data?.detail_object;
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 &&
          (structured?.error === 'mfa_step_up_required' || detail?.error === 'mfa_step_up_required')) {
        setStepUpOpen(true);
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Reset failed');
      }
    } finally {
      setResetting(false);
    }
  }, [target, reason, load]);

  if (rows === null) return null;

  return (
    <Card className="border-slate-200" data-testid="mfa-status-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldWarning size={20} className="text-indigo-700" /> User MFA Status
        </CardTitle>
        <CardDescription>
          MFA is mandatory for every account. Resetting a user&apos;s MFA revokes all their
          sessions and forces fresh enrollment at their next sign-in. Secrets, QR codes and
          recovery codes are never viewable.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>MFA Status</TableHead>
              <TableHead>Enrolled</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`mfa-user-row-${u.email}`}>
                <TableCell>
                  <div className="text-sm font-medium text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </TableCell>
                <TableCell className="text-sm">{u.role || u.entity_type}</TableCell>
                <TableCell>{statusBadge(u.mfa_status)}</TableCell>
                <TableCell className="text-xs text-slate-500">
                  {u.mfa_enrolled_at ? new Date(u.mfa_enrolled_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell className="text-right">
                  {u.id === me?.id ? (
                    <span className="text-xs text-slate-400">Use MFA Settings</span>
                  ) : (
                    <Button
                      variant="outline" size="sm"
                      disabled={u.mfa_status === 'Not Enrolled'}
                      onClick={() => { setTarget(u); setReason(''); }}
                      data-testid={`mfa-reset-btn-${u.email}`}
                    >
                      <ArrowCounterClockwise size={14} className="mr-1" /> Reset MFA
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!target} onOpenChange={(open) => { if (!open) setTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="mfa-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset MFA for {target?.email}?</DialogTitle>
            <DialogDescription>
              This immediately signs the user out of every session, permanently discards
              their authenticator and recovery codes, and forces fresh MFA enrollment at
              their next password login. This action is audited.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="reset-reason">
              Reason (required)
            </label>
            <Textarea
              id="reset-reason"
              placeholder="e.g. User lost their phone — identity verified via callback"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="mfa-reset-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={resetting || reason.trim().length < 5}
              onClick={doReset}
              data-testid="mfa-reset-confirm-btn"
            >
              {resetting ? 'Resetting…' : 'Reset MFA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MfaStepUpDialog open={stepUpOpen} onOpenChange={setStepUpOpen} onVerified={doReset} />
    </Card>
  );
};

export default MfaStatusPanel;
